import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, ExternalLink, Edit3, RefreshCw, Star, StarOff, Bookmark, Trash2, Plus, Folder, Grid3x3, BarChart3, FileText, ClipboardList, Palette, Blocks } from 'lucide-react';
export function ItemContextMenu({ item, itemType, onAction, trigger }) {
    const [isOpen, setIsOpen] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showCreateSubmenu, setShowCreateSubmenu] = useState(false);
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const submenuTriggerRef = useRef(null);
    const createSubmenuRef = useRef(null);
    const colorPickerRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(event) {
            const target = event.target;
            // Check if click is outside all menu elements (including portaled submenus)
            const clickedOutside = menuRef.current && !menuRef.current.contains(target) &&
                (!createSubmenuRef.current || !createSubmenuRef.current.contains(target)) &&
                (!colorPickerRef.current || !colorPickerRef.current.contains(target));
            if (clickedOutside) {
                setIsOpen(false);
                setShowColorPicker(false);
                setShowCreateSubmenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const calculateMenuPosition = (triggerElement) => {
        const rect = triggerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const menuWidth = 224; // w-56 = 14rem = 224px
        const menuMaxHeight = 384; // max-h-96 = 24rem = 384px
        let top = rect.bottom + 4; // Default: below trigger
        let left = rect.right - menuWidth; // Default: align right edge
        // Adjust if menu would go off bottom of screen
        if (top + menuMaxHeight > viewportHeight) {
            top = Math.max(8, viewportHeight - menuMaxHeight - 8);
        }
        // Adjust if menu would go off left of screen
        if (left < 8) {
            left = rect.left;
        }
        // Adjust if menu would go off right of screen
        if (left + menuWidth > viewportWidth - 8) {
            left = viewportWidth - menuWidth - 8;
        }
        return { top, left };
    };
    const calculateSubmenuPosition = (triggerElement) => {
        const rect = triggerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const submenuWidth = 176; // w-44 = 11rem = 176px
        const submenuMaxHeight = 384;
        let top = rect.top;
        let left = rect.right + 4; // Default: to the right
        // Flip to left if would go off right edge
        if (left + submenuWidth > viewportWidth - 8) {
            left = rect.left - submenuWidth - 4;
        }
        // Adjust if menu would go off bottom
        if (top + submenuMaxHeight > viewportHeight) {
            top = Math.max(8, viewportHeight - submenuMaxHeight - 8);
        }
        return { top, left };
    };
    const handleSubmenuToggle = (action, event) => {
        const target = event.currentTarget;
        const position = calculateSubmenuPosition(target);
        setSubmenuPosition(position);
        if (action === 'create-in-folder') {
            setShowCreateSubmenu(!showCreateSubmenu);
            setShowColorPicker(false);
        }
        else if (action === 'change-color') {
            setShowColorPicker(!showColorPicker);
            setShowCreateSubmenu(false);
        }
    };
    const handleAction = (action, data) => {
        console.log('🔵 ItemContextMenu handleAction:', action, 'data:', data, 'item:', item);
        onAction(action, { ...item, ...data });
        setIsOpen(false);
        setShowColorPicker(false);
        setShowCreateSubmenu(false);
    };
    const FOLDER_COLORS = [
        '#579bfc', // Blue
        '#00c875', // Green
        '#ff9f40', // Orange
        '#e2445c', // Red
        '#9d34da', // Purple
        '#fdab3d', // Yellow
    ];
    const createOptions = [
        { type: 'board', icon: Grid3x3, label: 'Board' },
        { type: 'dashboard', icon: BarChart3, label: 'Dashboard' },
        { type: 'doc', icon: FileText, label: 'Doc' },
        { type: 'form', icon: ClipboardList, label: 'Form' },
        { type: 'folder', icon: Folder, label: 'Folder' },
        { type: 'app', icon: Blocks, label: 'App' },
    ];
    const menuItems = () => {
        const common = [
            {
                icon: ExternalLink,
                label: 'Open in new tab',
                action: 'open-new-tab'
            },
            {
                icon: Edit3,
                label: 'Rename',
                action: 'rename'
            }
        ];
        if (itemType === 'board') {
            const board = item;
            return [
                ...common,
                {
                    icon: RefreshCw,
                    label: 'Change type',
                    action: 'change-type'
                },
                {
                    icon: board.starred ? StarOff : Star,
                    label: board.starred ? 'Remove from favorites' : 'Add to favorites',
                    action: 'toggle-favorite'
                },
                {
                    icon: Bookmark,
                    label: 'Save as template',
                    action: 'save-template'
                },
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Remove',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        else if (itemType === 'folder') {
            return [
                ...common,
                {
                    icon: Plus,
                    label: 'Create in folder',
                    action: 'create-in-folder',
                    hasSubmenu: true
                },
                {
                    icon: Palette,
                    label: 'Change color',
                    action: 'change-color',
                    hasSubmenu: true
                },
                {
                    icon: Bookmark,
                    label: 'Save as template',
                    action: 'save-template'
                },
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Delete folder',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        else if (itemType === 'doc') {
            const doc = item;
            return [
                ...common,
                {
                    icon: doc.starred ? StarOff : Star,
                    label: doc.starred ? 'Remove from favorites' : 'Add to favorites',
                    action: 'toggle-favorite'
                },
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Delete doc',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        else if (itemType === 'dashboard') {
            return [
                ...common,
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Delete dashboard',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        else if (itemType === 'form') {
            return [
                ...common,
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Delete form',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        else if (itemType === 'app') {
            const app = item;
            return [
                ...common,
                {
                    icon: app.starred ? StarOff : Star,
                    label: app.starred ? 'Remove from favorites' : 'Add to favorites',
                    action: 'toggle-favorite'
                },
                { divider: true },
                {
                    icon: Trash2,
                    label: 'Remove app',
                    action: 'remove',
                    danger: true
                }
            ];
        }
        // Default fallback
        return common;
    };
    const handleToggleMenu = () => {
        if (!isOpen && triggerRef.current) {
            const position = calculateMenuPosition(triggerRef.current);
            setMenuPosition(position);
        }
        setIsOpen(!isOpen);
    };
    return (_jsxs("div", { className: "relative", ref: triggerRef, children: [trigger ? (_jsx("div", { onClick: handleToggleMenu, children: trigger })) : (_jsx("button", { onClick: handleToggleMenu, className: "opacity-0 group-hover:opacity-100 p-1 hover:bg-white/08 rounded transition-opacity", children: _jsx(MoreHorizontal, { className: "w-3 h-3 icon-adaptive-muted" }) })), isOpen && createPortal(_jsx("div", { ref: menuRef, className: "fixed w-56 max-h-96 overflow-y-auto liquid-glass-sidebar border border-white/10 rounded-lg shadow-lg z-[99998]", style: {
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`
                }, children: _jsx("div", { className: "py-1", children: menuItems().map((menuItem, index) => {
                        if ('divider' in menuItem) {
                            return _jsx("div", { className: "border-t border-white/10 my-1" }, index);
                        }
                        const Icon = menuItem.icon;
                        return (_jsx("div", { className: "relative", children: _jsxs("button", { ref: menuItem.hasSubmenu ? submenuTriggerRef : undefined, onClick: (e) => {
                                    if (menuItem.action === 'create-in-folder' || menuItem.action === 'change-color') {
                                        handleSubmenuToggle(menuItem.action, e);
                                    }
                                    else {
                                        handleAction(menuItem.action);
                                    }
                                }, className: `w-full flex items-center px-3 py-2 text-left hover:bg-white/05 transition-colors ${menuItem.danger ? 'text-red-600 hover:bg-red-50' : 'text-adaptive-primary'}`, children: [_jsx(Icon, { className: "w-4 h-4 mr-3" }), _jsx("span", { className: "text-sm", children: menuItem.label }), menuItem.hasSubmenu && (_jsx("svg", { className: "w-3 h-3 ml-auto", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" }) }))] }) }, index));
                    }) }) }), document.body), showCreateSubmenu && createPortal(_jsx("div", { ref: createSubmenuRef, className: "fixed w-44 max-h-96 overflow-y-auto liquid-glass-sidebar border border-white/10 rounded-lg shadow-lg z-[99999]", style: {
                    top: `${submenuPosition.top}px`,
                    left: `${submenuPosition.left}px`
                }, children: _jsxs("div", { className: "py-1", children: [_jsxs("div", { className: "px-3 py-2 text-xs font-medium text-adaptive-muted border-b border-white/10", children: ["Create in ", item.name] }), createOptions.map((option) => {
                            const OptionIcon = option.icon;
                            return (_jsxs("button", { onClick: () => handleAction('create-in-folder', { createType: option.type }), className: "w-full flex items-center px-3 py-2 text-left hover:bg-white/05 transition-colors", children: [_jsx(OptionIcon, { className: "w-4 h-4 mr-3 icon-adaptive-muted" }), _jsx("span", { className: "text-sm text-adaptive-primary", children: option.label })] }, option.type));
                        })] }) }), document.body), showColorPicker && createPortal(_jsx("div", { ref: colorPickerRef, className: "fixed w-36 max-h-96 overflow-y-auto liquid-glass-sidebar border border-white/10 rounded-lg shadow-lg z-[99999]", style: {
                    top: `${submenuPosition.top}px`,
                    left: `${submenuPosition.left}px`
                }, children: _jsxs("div", { className: "py-3 px-3", children: [_jsx("div", { className: "text-xs font-medium text-adaptive-muted mb-3", children: "Choose color" }), _jsx("div", { className: "grid grid-cols-3 gap-2", children: FOLDER_COLORS.map((color) => (_jsx("button", { onClick: () => handleAction('change-color', { color }), className: `w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${item.color === color ? 'border-white/40' : 'border-white/20'}`, style: { backgroundColor: color } }, color))) })] }) }), document.body)] }));
}
